"use client";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setCurrentHospital,
  setHospitals,
  clearHospitals,
} from "../app/currentHospital";
import {
  getHospitalsByParentName,
  clearParentHospitalInfo,
} from "../api/hospitals";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ChevronDown, CreditCard } from "lucide-react";
import { Settings, LogOut, Menu, X, Plus } from "lucide-react";
import { useCreateShift } from "../contexts/CreateShiftContext";
import { message } from "antd";
import { reconnectForHospitalContextChange } from "../realtime/realtimeClient";
import { alterUserInfo } from "../app/userInfo";
import apiLogin from "../api/login";
import api from "../api/account";
import Confirm from "./common/confirm";
import NotificationPanel from "./NotificationPanel";
import { MessagesPanel } from "./MessagesPanel";
import { useSocialUnreadCount } from "../hooks/useSocialUnreadCount";
import { useBankCounts } from "./bank/useBankCounts";
import { useNotificationsAdvanced } from "../hooks/useNotifications";
import { getHospitalUnreadCount } from "../realtime/notificationCountsStore";
import { useGrandmasterExperience } from "../contexts/GrandmasterExperienceContext";
import { hospitalSwitcherEntries } from "../utils/clinicNetworkSites";
import {
  accountRoleLabel,
  type HospitalAccessRole,
} from "../utils/hospitalAccessRole";
import HospitalSwitcherMenu from "./hospital/HospitalSwitcherMenu";
import { preloadPrimaryTabs } from "../router/preloadPrimaryTabs";

const TopNav = ({ hideRouteTabs = false }: { hideRouteTabs?: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isClinicNetworkExperience, networkRole } = useGrandmasterExperience();
  const accessRole: HospitalAccessRole = networkRole?.grandmaster
    ? "grandmaster"
    : networkRole?.masterRoot
      ? "master"
      : "hospital";
  const { openCreateShift } = useCreateShift();
  const device = useSelector((state: any) => state.commonplace.device);
  const navLayout = useSelector((state: any) => state.commonplace.navLayout);
  const hospitals = useSelector((state: any) => state.currentHospital.list);
  const currentHospital = useSelector(
    (state: any) => state.currentHospital.current,
  );

  // mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // hospital data state
  const [hospitalImage, setHospitalImage] = useState("");
  const [currentHospitalName, setCurrentHospitalName] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hospitalsLoading, setHospitalsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Notification logic for hospital dropdown
  const allHospitalIds = useMemo(() => {
    return hospitals?.map((h: any) => h.id) || [];
  }, [hospitals]);

  useNotificationsAdvanced("hospital", null, allHospitalIds);
  const { unreadCount: socialUnreadCount } = useSocialUnreadCount(true);
  const bankCounts = useBankCounts();
  // logout operate
  const [cancelInfo, setCancelInfo] = useState({
    show: false,
    loading: false,
    title: "",
    question: "",
  });

  const logoutOperate = () => {
    setCancelInfo({
      show: true,
      loading: false,
      title: "Logout?",
      question: "Are you sure to log out of the current account?",
    });
  };

  const logoutConfirm = () => {
    console.log("🚪 LOGOUT: Starting logout process");

    // Close the modal immediately and show loader
    setCancelInfo({
      show: false,
      loading: true,
      title: "",
      question: "",
    });

    // Add a delay to ensure loader is visible before making API call
    setTimeout(() => {
      console.log("🚪 LOGOUT: Making API call after delay");
      apiLogin.signOut({
        callback: (res: any) => {
          console.log("🚪 LOGOUT: API response received:", res);
          if (res.data.code === 200) {
            console.log(
              "🚪 LOGOUT: API success, clearing storage and updating state",
            );
            // Clear parent hospital info from localStorage
            clearParentHospitalInfo();
            // Clear master token
            localStorage.removeItem("master-token");
            // Clear hospitals from Redux state
            dispatch(clearHospitals());
            // Clear all storage
            localStorage.clear();
            sessionStorage.clear();

            // Then update Redux state
            dispatch(
              alterUserInfo({
                loginStatus: false,
              }),
            );

            // Stop loading and navigate
            setCancelInfo({
              show: false,
              loading: false,
              title: "",
              question: "",
            });
            console.log("🚪 LOGOUT: Navigating to login");
            navigate("/login");
          } else {
            console.log("🚪 LOGOUT: API error:", res.message);
            message.error(res.message);
            setCancelInfo({
              show: false,
              loading: false,
              title: "",
              question: "",
            });
          }
        },
      });
    }, 1000); // 1 second delay to ensure loader is visible
  };

  const navItems = [
    { name: "Dashboard", path: "/dashboard", disabled: false },
    { name: "Calendar", path: "/calendar", disabled: false },
    { name: "Bank", path: "/bank", disabled: false },
    { name: "Social", path: "/social", disabled: false },
    { name: "Finance", path: "/finance", disabled: true },
    { name: "Tools", path: "/tools", disabled: false },
    { name: "Profile", path: "/account", disabled: false },
  ];

  const isActive = (path: string) => {
    const currentPath = location.pathname;

    if (path === "/dashboard") {
      return currentPath === "/dashboard" || currentPath === "/overview";
    }
    if (path === "/finance") {
      return currentPath.includes("/finance");
    }

    // Calendar specific check
    if (path === "/calendar") {
      return currentPath === "/calendar";
    }
    if (path === "/bank") {
      return currentPath.includes("/bank");
    }

    // Hire specific check - exact match only
    if (path === "/hire") {
      return currentPath === "/hire" || currentPath.startsWith("/hire/");
    }


    // Keep Profile active on account-related routes
    if (path === "/account") {
      return (
        currentPath.includes("/account") ||
        currentPath.includes("/modify")
      );
    }
    if (path === "/social") {
      return currentPath.includes("/social");
    }
    // Consider any /legacy-shifts sub-route as Legacy Shifts active
    if (path.startsWith("/legacy-shifts")) {
      // Don't highlight "Legacy Shifts" if we are actually on "Calendar" or other pages
      if (currentPath === "/calendar" || currentPath.startsWith("/hire")) {
        return false;
      }

      const shiftsRelated = [
        "/legacy-shifts",
        "/create",
        "/alter/",
        "/detail/",
        "/applicants/",
        "/applicant/",
        "/doctor/",
      ];
      return shiftsRelated.some((p) => currentPath.includes(p));
    }
    if (path === "/tools" || path.startsWith("/vault")) {
      return (
        currentPath.includes("/tools") ||
        currentPath.includes("/vault")
      );
    }
    // Consider any /social sub-route as Social active
    if (path.startsWith("/social")) {
      return currentPath.includes("/social");
    }
    return currentPath.includes(path);
  };

  // Function to fetch hospital info (image, background, name)
  const fetchHospitalInfo = (shouldFetchHierarchy = true) => {
    const token = localStorage.getItem("token-name");
    let isDelegatedMember = false;
    try {
      const orgProfileRaw = localStorage.getItem("org-session-profile");
      const orgProfile = orgProfileRaw ? JSON.parse(orgProfileRaw) : null;
      isDelegatedMember = Boolean(orgProfile?.delegatedMember);
    } catch {}

    if (token) {
      api.actInfo({
        callback: async (res: any) => {
          if (res && res.data && res.data.code === 200) {
            setHospitalImage(res.data.data.hospitalImage);
            setCurrentHospitalName(res.data.data.hospitalName);

            const accountId = res.data.data.accountId;

            // Store current hospital info for potential parent reference
            const currentHospitalInfo = {
              id: accountId,
              name: res.data.data.hospitalName,
            };

            if (!isDelegatedMember) {
              localStorage.setItem(
                `current-hospital-info`,
                JSON.stringify(currentHospitalInfo),
              );
            }

            if (!currentHospital) {
              if (isDelegatedMember) {
                try {
                  const orgProfileRaw = localStorage.getItem("org-session-profile");
                  const orgProfile = orgProfileRaw ? JSON.parse(orgProfileRaw) : null;
                  const accessible = orgProfile?.accessibleHospitals || [];
                  let persistedId: string | undefined;
                  try {
                    const raw = localStorage.getItem("current-hospital");
                    persistedId = raw ? JSON.parse(raw)?.id : undefined;
                  } catch {}
                  const selected =
                    accessible.find(
                      (h: { id: string }) => h.id === persistedId,
                    ) ||
                    accessible.find(
                      (h: { id: string }) => h.id === orgProfile?.homeHospitalId,
                    ) ||
                    accessible[0];
                  if (selected?.id) {
                    const initialHospital = {
                      id: selected.id,
                      name: selected.name || "Hospital",
                    };
                    dispatch(setCurrentHospital(initialHospital));
                    if (!hospitals || hospitals.length === 0) {
                      dispatch(setHospitals(accessible));
                    }
                  }
                } catch {}
              } else {
                const initialHospital = {
                  id: accountId,
                  name: res.data.data.hospitalName,
                  hospitalImage: res.data.data.hospitalImage,
                };
                dispatch(setCurrentHospital(initialHospital));
                if (!hospitals || hospitals.length === 0) {
                  dispatch(setHospitals([initialHospital]));
                }
              }
            }

            // Only fetch hierarchy if requested (e.g. on initial load, but NOT on switch)
            if (shouldFetchHierarchy && !isDelegatedMember) {
              // NOW fetch the hierarchy
              // This ensures we get the Master's hierarchy even if we were viewing a slave
              setHospitalsLoading(true);
              try {
                /*   console.log(
                  "🏥 NAV: Fetching fresh hospital hierarchy for account:",
                  accountId,
                );*/
                // Force refresh (true)
                const list = await getHospitalsByParentName("", true);

                if (list && list.length > 0) {
                  /*     console.log(
                    "🏥 NAV: Loaded fresh hospital hierarchy:",
                    list.length,
                    "hospitals",
                  );*/
                  dispatch(setHospitals(list));

                  // Self-healing: If we are the master (root), ensure master-token is saved
                  // This handles cases where the user was already logged in before the master-token logic was added
                  if (list.length > 1 && accountId === list[0].id) {
                    const currentToken = localStorage.getItem("token-name");
                    const existingMaster = localStorage.getItem("master-token");
                    if (currentToken && (!existingMaster || existingMaster === "null")) {
                      localStorage.setItem("master-token", currentToken);
                    }
                  }

                  // If we have a list, ensure currentHospital is one of them (preferably the first/master)
                  // Only override if the current one is missing or we want to enforce master view
                  if (!currentHospital) {
                    dispatch(setCurrentHospital(list[0]));
                  }
                } else {
                  console.warn("🏥 NAV: Hierarchy API returned empty list");
                }
              } catch (error) {
                console.error(
                  "🏥 NAV: Error loading hospital hierarchy:",
                  error,
                );
              } finally {
                setHospitalsLoading(false);
              }
            }
          }
        },
      });
    }
  };

  // Fetch hospital data (image/background) and get hospital name for dropdown
  useEffect(() => {
    fetchHospitalInfo(true); // Fetch hierarchy on mount
  }, []); // Run once on mount

  // preload hospitals from API (parent-with-child)
  // REMOVED: Merged into actInfo callback to ensure correct context
  /*
  useEffect(() => {
    const loadHospitals = async () => {
      // ...
    };
    loadHospitals();
  }, [dispatch]); 
  */

  const switcherHospitals = hospitalSwitcherEntries(hospitals);

  useEffect(() => {
    if (!dropdownOpen) setSearchTerm("");
  }, [dropdownOpen]);

  const persistSwitchedHospital = (h: any) => {
    dispatch(setCurrentHospital(h));
    if (!h?.id) return;
    localStorage.setItem("current-hospital", JSON.stringify(h));
    localStorage.setItem("selected-hospital-id", String(h.id));
    localStorage.setItem(
      "current-hospital-info",
      JSON.stringify({ id: h.id, name: h.name }),
    );
    try {
      sessionStorage.setItem("dashboard-grandmaster-scope", String(h.id));
    } catch {
      // ignore
    }
  };

  const selectHospital = (h: any) => {
    if (h.id !== currentHospital?.id) {
      let orgProfile: any = null;
      try {
        const orgProfileRaw = localStorage.getItem("org-session-profile");
        orgProfile = orgProfileRaw ? JSON.parse(orgProfileRaw) : null;
      } catch {}

      if (orgProfile?.delegatedMember) {
        const nextPermissions =
          orgProfile.permissionsByHospital?.[h.id] ||
          orgProfile.permissions ||
          [];
        localStorage.setItem(
          "org-session-profile",
          JSON.stringify({
            ...orgProfile,
            permissions: nextPermissions,
          }),
        );
        persistSwitchedHospital(h);
        setDropdownOpen(false);
        return;
      }

      const parentId = (() => {
        try {
          const raw = localStorage.getItem("persistent-parent-hospital");
          return raw && raw !== "null" ? String(JSON.parse(raw)?.id || "") : "";
        } catch {
          return "";
        }
      })();
      const loginToken = localStorage.getItem("master-token");
      if (loginToken && parentId && String(h.id) === parentId) {
        localStorage.setItem("token-name", loginToken);
        persistSwitchedHospital(h);
        setDropdownOpen(false);
        return;
      }

      setHospitalsLoading(true);
      api.tokenGenerator({
        accountId: h.id,
        callback: (res: any) => {
          const errorCode = res?.data?.code;
          if (
            errorCode === 90000 ||
            errorCode === 90001 ||
            errorCode === 90002 ||
            errorCode === 90003 ||
            errorCode === 90004 ||
            errorCode === 90005
          ) {
            alert(
              "Unable to authorize hospital switch. Your session might be too old. Please log out and log back in to restore this feature.",
            );
            setHospitalsLoading(false);
            setDropdownOpen(false);
            return;
          }

          if (res && res.data && res.data.code === 200) {
            const token =
              res.headers["doctor-admin-token"] ||
              res.headers["Doctor-Admin-Token"];

            if (token && token !== "null") {
              localStorage.setItem("token-name", token);
            } else {
              alert(
                `Hospital "${h.name}" is not available for selection. Please contact support.`,
              );
              setHospitalsLoading(false);
              return;
            }
          } else {
            alert(`Unable to switch to "${h.name}". Please try again later.`);
            setHospitalsLoading(false);
            return;
          }
          persistSwitchedHospital(h);

          fetchHospitalInfo(false);

          if (currentHospital) {
            localStorage.setItem(
              `parent-hospital-${currentHospital.id}`,
              JSON.stringify({
                id: currentHospital.id,
                name: currentHospital.name,
              }),
            );
          }

          setDropdownOpen(false);
          reconnectForHospitalContextChange();

          const currentPath = window.location.pathname;
          if (currentPath === "/login") {
            navigate("/calendar", { state: { refresh: Date.now() } });
          }
          setHospitalsLoading(false);
        },
      });
      return;
    }

    dispatch(setCurrentHospital(h));
    setDropdownOpen(false);
    navigate("/calendar", { state: { refresh: Date.now() } });
  };

  const token = localStorage.getItem("token-name");
  if (!token) return null;

  return (
    <>
      <nav className="glass fixed inset-x-0 top-0 z-40 flex h-14 items-center overflow-hidden px-3 md:px-4">
        <>
          {/* Desktop Layout */}
          <div className="hidden md:flex w-full items-center justify-between relative">
            {/* Left branding + Hospital selector */}
            <div className="flex min-w-0 items-center gap-2 md:gap-4">
              <button
                type="button"
                className="flex min-h-11 flex-none items-center gap-2"
                onClick={() => {
                  const token = localStorage.getItem("token-name");
                  navigate(token ? "/calendar" : "/home");
                }}
                aria-label="Go to calendar"
              >
                <img
                  src="/brand/stathospital-mark.svg"
                  alt=""
                  className="h-[23px] w-auto"
                />
                <span className="font-display text-[18px] font-bold tracking-[-0.03em] text-foreground">
                  Stat<span className="text-[#D8A665]">Hospital</span>
                </span>
              </button>
              <span className="hidden h-6 w-px bg-border md:block" />
              <div>
                {/* offset due to absolute logo */}
                {/* offset due to absolute logo */}
                {(() => {
                  const hasAlternatives =
                    !isClinicNetworkExperience &&
                    Array.isArray(hospitals) &&
                    hospitals.length > 1;

                  // Calculate if any other hospital (not current) has notifications
                  const hasUnreadOther = (hospitals || []).some(
                    (h: any) =>
                      String(h.id) !== String(currentHospital?.id) &&
                      getHospitalUnreadCount(h.id) > 0,
                  );

                  const triggerSubtitle = accountRoleLabel(
                    currentHospital || switcherHospitals[0],
                    accessRole,
                    switcherHospitals[0]?.id,
                    switcherHospitals,
                  );

                  if (!hasAlternatives) {
                    return (
                      <div className="flex h-11 min-w-0 flex-col justify-center rounded-xl bg-white px-3 text-left hairline select-none">
                        <span className="truncate text-[13px] font-semibold leading-4 text-foreground">
                          {currentHospital?.name ||
                            currentHospitalName ||
                            "Hospital"}
                        </span>
                        <span className="truncate text-[11px] leading-4 text-muted-foreground">
                          {accountRoleLabel(
                            currentHospital || switcherHospitals[0],
                            accessRole,
                            switcherHospitals[0]?.id,
                            switcherHospitals,
                          )}
                        </span>
                      </div>
                    );
                  }
                  return (
                    <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
                      <PopoverTrigger asChild>
                        <button className={`relative flex h-11 min-w-0 items-center gap-2 rounded-xl bg-white px-3 text-left hairline ${dropdownOpen ? "ring-2 ring-[#023eff]/20" : ""}`}>
                          {hospitalsLoading ? (
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-32 rounded bg-gray-100 animate-pulse" />
                            </div>
                          ) : (
                            <>
                              {hasUnreadOther ? (
                                <span className="absolute right-8 top-2 h-2 w-2 rounded-full bg-red-500" />
                              ) : null}
                              <span className="min-w-0">
                                <span className="block truncate text-[13px] font-semibold leading-4 text-foreground">
                                  {currentHospital?.name || "Select hospital"}
                                </span>
                                <span className="block truncate text-[11px] leading-4 text-muted-foreground">
                                  {triggerSubtitle}
                                </span>
                              </span>
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                            </>
                          )}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        className="w-auto border-0 bg-transparent p-0 shadow-none"
                      >
                        <HospitalSwitcherMenu
                          hospitals={switcherHospitals}
                          networkHospitals={hospitals}
                          currentHospitalId={currentHospital?.id}
                          searchTerm={searchTerm}
                          onSearchTermChange={setSearchTerm}
                          onSelect={selectHospital}
                          accessRole={accessRole}
                        />

                      </PopoverContent>
                    </Popover>
                  );
                })()}
              </div>
            </div>
            {/* <div className="text-background font-bold text-sm tracking-wide">
            The Avenue Hospital
          </div> */}
            {/* Right section: Center pills and Right actions */}
            <div className="flex items-center gap-6">
              {/* Center pills */}
              {!hideRouteTabs && (
                <div className="relative overflow-visible bg-white rounded-full px-2 h-10 flex items-center gap-0 shadow-lg rounded-tr-xl rounded-bl-xl">
                  {navItems.map((item) => {
                    const active = isActive(item.path);
                    const showSocialBadge =
                      item.name === "Social" &&
                      !item.disabled &&
                      socialUnreadCount > 0;
                    const showBankBadge =
                      item.name === "Bank" &&
                      !item.disabled &&
                      bankCounts.totalRequests > 0;
                    return (
                      <button
                        key={item.name}
                        onMouseEnter={() => {
                          if (!item.disabled) preloadPrimaryTabs();
                        }}
                        onClick={() => !item.disabled && navigate(item.path)}
                        disabled={item.disabled}
                        title={
                          item.disabled && (item as any).tooltip
                            ? (item as any).tooltip
                            : undefined
                        }
                        className={[
                          "relative -ml-2 -mr-2 px-4 py-[10px] text-sm font-medium transition-all duration-200 rounded-full",
                          active
                            ? "leaf-active bg-primary text-primary-foreground shadow-[0_6px_14px_rgba(216,166,101,.25)] rounded-tr-2xl rounded-bl-2xl px-8 hover:scale-105 transition-all duration-200"
                            : item.disabled
                              ? "text-gray-400 cursor-not-allowed rounded-tr-2xl rounded-bl-2xl px-8 opacity-50"
                              : "text-gray-600 hover:text-primary hover:bg-primary/10 rounded-tr-2xl rounded-bl-2xl px-8",
                        ].join(" ")}
                      >
                        {item.name}
                        {showSocialBadge && (
                          <span className="absolute -top-0.5 right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
                            {socialUnreadCount > 99 ? "99+" : socialUnreadCount}
                          </span>
                        )}
                        {showBankBadge && (
                          <span className="absolute -top-0.5 right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
                            {bankCounts.totalRequests > 99
                              ? "99+"
                              : bankCounts.totalRequests}
                          </span>
                        )}
                        {item.disabled && item.name === "Master" && (
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-gray-400"></span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Right actions */}
              <div className="flex items-center gap-4 ml-4 shrink-0">
                <button
                  type="button"
                  onClick={() => openCreateShift()}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3.5 text-[13px] font-medium text-white hover:bg-primary/90"
                >
                  <Plus size={14} />
                  Post a shift
                </button>
                <button
                  className="flex items-center justify-center w-9 h-9 text-muted-foreground transition-colors rounded-md hover:bg-white relative cursor-not-allowed"
                  disabled
                  title="Payment notifications (Currently unavailable)"
                >
                  <CreditCard className="w-5 h-5" />
                </button>
                {navLayout !== "professional" ? <MessagesPanel /> : null}
                <NotificationPanel />
                <button
                  onClick={logoutOperate}
                  className="flex items-center justify-center w-9 h-9 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-white"
                >
                  {cancelInfo.loading ? (
                    <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
                  ) : (
                    <LogOut className="w-5 h-5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/account")}
                  title={currentHospitalName || currentHospital?.name || "Account"}
                  className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full border border-[#e6e9f0] bg-[#eef2ff] text-[11px] font-semibold text-primary"
                >
                  {hospitalImage ? (
                    <img
                      src={hospitalImage}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    (currentHospitalName || currentHospital?.name || "SH")
                      .trim()
                      .split(/\s+/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((part: string) => part[0])
                      .join("")
                      .toUpperCase() || "SH"
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden flex items-center justify-between px-3 py-2">
            {/* Left: Logo only */}
            <div className="flex items-center">
              <div
                className="logo-leaf px-2 py-1 rounded-full flex items-center bg-white cursor-pointer"
                onClick={() => {
                  const token = localStorage.getItem("token-name");
                  navigate(token ? "/calendar" : "/home");
                }}
                aria-label="Go to home"
              >
                <img
                  src="/logo-login.svg"
                  alt="StatDoctor"
                  className="h-4 w-auto"
                />
              </div>
            </div>

            {/* Center: Current page name */}
            <div className="flex-1 flex justify-center">
              <span className="text-foreground font-medium text-xs">
                {(() => {
                  const currentPath = location.pathname;
                  if (currentPath.includes("/detail/")) return "Shift Details";
                  if (currentPath.includes("/shifts")) return "Shifts";
                  if (currentPath.includes("/doctors")) return "Doctors";
                  if (currentPath.includes("/tools") || currentPath.includes("/vault")) return "Tools";
                  if (currentPath.includes("/bank")) return "Bank";
                  if (currentPath.includes("/account")) return "Profile";
                  if (currentPath.includes("/social")) return "Social";
                  if (currentPath.includes("/calendar")) return "Calendar";
                  if (currentPath.includes("/dashboard") || currentPath.includes("/overview")) return "Dashboard";
                  if (currentPath.includes("/finance")) return "Finance";
                  return "StatDoctor";
                })()}
              </span>
            </div>

            {/* Right: Menu, hospital image and logout */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => navigate("/account")}
                title={currentHospitalName || currentHospital?.name || "Account"}
                className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full border border-[#e6e9f0] bg-[#eef2ff] text-[10px] font-semibold text-primary"
              >
                {hospitalImage ? (
                  <img
                    src={hospitalImage}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  (currentHospitalName || currentHospital?.name || "SH")
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((part: string) => part[0])
                    .join("")
                    .toUpperCase() || "SH"
                )}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-1.5 text-white hover:text-white/80 transition-colors bg-white/20 rounded-full"
              >
                {mobileMenuOpen ? (
                  <X className="w-3 h-3" />
                ) : (
                  <Menu className="w-3 h-3" />
                )}
              </button>
              <button
                onClick={logoutOperate}
                className="p-1.5 text-white hover:text-white/80 transition-colors bg-white/20 rounded-full"
              >
                {cancelInfo.loading ? (
                  <div className="w-3 h-3 bg-white/30 rounded animate-pulse"></div>
                ) : (
                  <LogOut className="w-3 h-3" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden absolute top-full left-2 right-2 sm:left-4 sm:right-4 mt-1 sm:mt-2 bg-card rounded-xl shadow-lg border border-border py-1.5 sm:py-2">
              {navItems.map((item) => {
                const active = isActive(item.path);
                const showSocialBadge =
                  item.name === "Social" &&
                  !item.disabled &&
                  socialUnreadCount > 0;
                const showBankBadge =
                  item.name === "Bank" &&
                  !item.disabled &&
                  bankCounts.totalRequests > 0;
                return (
                  <button
                    key={item.name}
                    onClick={() => {
                      if (!item.disabled) {
                        navigate(item.path);
                        setMobileMenuOpen(false);
                      }
                    }}
                    disabled={item.disabled}
                    className={`relative w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors ${
                      active
                        ? "bg-primary/10 text-primary border-r-2 border-primary"
                        : item.disabled
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-foreground hover:bg-muted/50"
                    }`}
                  >
                    {item.name}
                    {showSocialBadge && (
                      <span className="ml-2 inline-flex min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold items-center justify-center align-middle">
                        {socialUnreadCount > 99 ? "99+" : socialUnreadCount}
                      </span>
                    )}
                    {showBankBadge && (
                      <span className="ml-2 inline-flex min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold items-center justify-center align-middle">
                        {bankCounts.totalRequests > 99
                          ? "99+"
                          : bankCounts.totalRequests}
                      </span>
                    )}
                  </button>
                );
              })}
              <div className="border-t border-border mt-1.5 sm:mt-2 pt-1.5 sm:pt-2">
                <button
                  onClick={() => {
                    navigate("/account");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-foreground hover:bg-muted/50 flex items-center gap-2"
                >
                  <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Account
                </button>
                <div className="px-3 sm:px-4 py-1.5 sm:py-2 flex items-center justify-between">
                  {navLayout !== "professional" ? <MessagesPanel /> : null}
                  <NotificationPanel />
                  <button
                    className="flex items-center justify-center p-2 text-gray-300 transition-colors bg-white rounded-full shadow-md cursor-not-allowed mr-1"
                    disabled
                    title="Payment notifications (Currently unavailable)"
                  >
                    <CreditCard className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
          <Confirm
            info={cancelInfo}
            setIsModalOpen={setCancelInfo}
            submitEvt={logoutConfirm}
            width={420}
            btns={["Cancel", "Confirm logout"]}
            device={device}
          />

          {/* Logout Loader Overlay */}
          {cancelInfo.loading &&
            console.log(
              "🚪 LOGOUT: Showing loader overlay, cancelInfo.loading:",
              cancelInfo.loading,
            )}
          {cancelInfo.loading && (
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-md transition-opacity duration-150"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <div className="flex flex-col items-center gap-4">
                {/* Lottie Player */}
                <lottie-player
                  autoplay
                  loop
                  mode="normal"
                  src="/loader.json"
                  style={{ width: "160px", height: "160px" }}
                ></lottie-player>

                {/* Loading Text */}
                <div className="text-white text-lg font-medium">
                  Logging out...
                </div>
              </div>
            </div>
          )}
        </>
      </nav>
    </>
  );
};

export default TopNav;
